<template>
  <!-- Backdrop for mobile -->
  <div v-if="sidebarVisible && isRenderSideBar" class="fixed inset-0 z-20 bg-slate-900/40 backdrop-blur-sm lg:hidden"
    @click="closeSidebar" />

  <aside v-if="isRenderSideBar" :class="[
    'fixed top-0 left-0 h-full z-30 flex flex-col bg-surface border-r border-line transition-all duration-300',
    collapsed ? 'lg:w-[72px]' : 'lg:w-[264px]',
    'w-[264px]',
    sidebarVisible ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
  ]">
    <!-- Brand / Logo -->
    <div class="flex items-center h-16 px-[18px] border-b border-line flex-shrink-0">
      <router-link to="/presupuestos/list" class="flex items-center gap-2.5 min-w-0">
        <img src="../../src/assets/logo/bm_icon.png" alt="Logo"
          class="h-9 w-9 rounded-lg flex-shrink-0 object-contain" />
        <span class="min-w-0" v-if="!collapsed">
          <span class="block truncate text-sm font-semibold text-ink">Budgety</span>
          <span class="block truncate text-[11px] text-muted">BM Software</span>
        </span>
      </router-link>
    </div>

    <!-- Nav -->
    <nav class="flex-1 overflow-y-auto overflow-x-hidden py-4 px-[18px]">
      <div v-for="(section, sIndex) in navItems" :key="section.title || sIndex" :class="{ 'mt-5': sIndex > 0 }">
        <!-- Section title (expanded) -->
        <p v-if="section.title && !collapsed"
          class="h-6 mb-1.5 flex items-center px-1 text-[10px] font-semibold uppercase tracking-widest text-muted/80">
          {{ section.title }}
        </p>
        <!-- Section divider (collapsed) -->
        <div v-if="section.title && collapsed" class="h-6 mb-1.5 flex items-center justify-center" aria-hidden="true">
          <div class="h-px w-9 bg-line/80" />
        </div>

        <ul class="space-y-0.5">
          <li v-for="item in section.items" :key="item.id">
            <!-- Independent link -->
            <router-link v-if="item.type === 'link'" :to="item.to || '#'" @mouseenter="openFlyout(item, $event)"
              @focus="openFlyout(item, $event)" @mouseleave="scheduleClose" @blur="scheduleClose"
              :aria-label="item.name" :class="[
                'relative flex h-9 items-center rounded-lg transition-colors min-w-0',
                isItemActive(item) ? 'bg-brandSoft text-brandInk' : 'text-muted hover:bg-surfaceAlt hover:text-ink',
              ]">
              <div class="flex h-9 w-9 items-center justify-center shrink-0">
                <component :is="iconMap[item.icon]" class="h-[18px] w-[18px]" />
              </div>
              <span v-if="!collapsed" class="truncate text-sm font-medium pr-2 ml-1">{{ item.name }}</span>
              <span v-if="collapsed && isItemActive(item)" class="absolute -left-1.5 h-5 w-1 rounded-r-full bg-brand"
                aria-hidden="true" />
            </router-link>

            <!-- Group accordion -->
            <div v-else>
              <button type="button" @click="collapsed ? null : toggleGroup(item.id)"
                @mouseenter="openFlyout(item, $event)" @focus="openFlyout(item, $event)" @mouseleave="scheduleClose"
                @blur="scheduleClose" :aria-label="item.name" :class="[
                  'relative flex h-9 w-full items-center rounded-lg transition-colors min-w-0',
                  isItemActive(item) ? (collapsed ? 'bg-brandSoft text-brandInk' : 'text-brandInk') : 'text-muted hover:bg-surfaceAlt hover:text-ink',
                ]">
                <div class="flex h-9 w-9 items-center justify-center shrink-0">
                  <component :is="iconMap[item.icon]" class="h-[18px] w-[18px]" />
                </div>
                <span v-if="!collapsed" class="flex-1 text-left truncate text-sm font-medium ml-1">{{ item.name
                  }}</span>
                <ChevronRight v-if="!collapsed"
                  :class="['w-3.5 h-3.5 flex-shrink-0 mr-2 transition-transform duration-200', openGroups.includes(item.id) ? 'rotate-90' : '']" />
                <span v-if="collapsed && isItemActive(item)" class="absolute -left-1.5 h-5 w-1 rounded-r-full bg-brand"
                  aria-hidden="true" />
              </button>
              <div v-if="!collapsed && openGroups.includes(item.id)"
                class="ml-[18px] mt-1 space-y-0.5 border-l border-line pl-3">
                <router-link v-for="child in item.items" :key="child.to" :to="child.to" :class="[
                  'block rounded-md px-2.5 py-1.5 text-sm transition-colors',
                  route.path === child.to ? 'bg-brandSoft font-medium text-brandInk' : 'text-muted hover:text-ink hover:bg-surfaceAlt',
                ]">
                  {{ child.name }}
                </router-link>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </nav>

    <!-- Footer -->
    <div class="flex h-14 items-center justify-between border-t border-line flex-shrink-0 mt-auto px-[18px]">
      <span v-if="!collapsed" class="text-[11px] text-muted">BM Labs © 2026</span>
      <button type="button"
        class="rounded-lg p-2 text-muted hover:bg-surfaceAlt hover:text-ink transition-colors lg:block hidden"
        :class="{ 'mx-auto': collapsed }" @click="toggleCollapse"
        :aria-label="collapsed ? 'Expandir menú' : 'Colapsar menú'"
        v-tooltip="collapsed ? 'Expandir menú' : 'Colapsar menú'">
        <PanelLeftOpen v-if="collapsed" class="w-4 h-4" />
        <PanelLeftClose v-else class="w-4 h-4" />
      </button>
    </div>
  </aside>

  <!-- Flyout submenu (visible only when collapsed) -->
  <div v-if="collapsed && flyout"
    class="fixed z-50 w-56 rounded-xl border border-line bg-surface p-2 shadow-flyout lg:block hidden"
    :style="{ top: flyout.top + 'px', left: '80px' }" @mouseenter="cancelClose" @mouseleave="scheduleClose" role="menu"
    :aria-label="flyout.item.name">
    <p class="px-2.5 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted">
      {{ flyout.item.name }}
    </p>
    <ul class="space-y-0.5">
      <li v-for="child in flyout.item.items || [{ name: flyout.item.name, to: flyout.item.to }]" :key="child.name">
        <router-link v-if="child.to" :to="child.to" @click="flyout = null" :class="[
          'block rounded-md px-2.5 py-2 text-sm transition-colors',
          route.path === child.to ? 'bg-brandSoft font-medium text-brandInk' : 'text-muted hover:bg-surfaceAlt hover:text-ink',
        ]" role="menuitem">
          {{ child.name }}
        </router-link>
        <span v-else class="block rounded-md px-2.5 py-2 text-sm text-muted cursor-default opacity-60" role="menuitem">
          {{ child.name }}
        </span>
      </li>
    </ul>
  </div>
</template>

<script>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import {
  CreditCard,
  BarChart2,
  Briefcase,
  Wallet,
  CheckCircle,
  Settings,
  MonitorCog,
  Activity,
  Users,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  ClipboardX,
  Receipt,
} from '@lucide/vue'
import navItems from '@/_nav.js'

const iconMap = {
  'credit-card': CreditCard,
  'bar-chart': BarChart2,
  briefcase: Briefcase,
  wallet: Wallet,
  'check-circle': CheckCircle,
  settings: Settings,
  monitor: MonitorCog,
  activity: Activity,
  users: Users,
  clipboardx: ClipboardX,
  bill: Receipt,
}

export default {
  name: 'AppSidebar',
  components: {
    ChevronRight,
    PanelLeftClose,
    PanelLeftOpen,
    ...iconMap,
  },
  setup() {
    const store = useStore()
    const route = useRoute()

    const isRenderSideBar = computed(() => store.state.isRenderSideBar)

    const isMobile = ref(typeof window !== 'undefined' ? window.innerWidth < 1024 : false)
    const updateIsMobile = () => {
      isMobile.value = window.innerWidth < 1024
    }

    onMounted(() => {
      window.addEventListener('resize', updateIsMobile)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateIsMobile)
    })

    const collapsed = computed(() => {
      if (isMobile.value) return false
      return store.state.sidebarUnfoldable
    })

    const sidebarVisible = computed(() => store.state.sidebarVisible)

    const closeSidebar = () => {
      store.commit('updateSidebarVisible', { value: false })
    }

    watch(
      () => route.path,
      () => {
        closeSidebar()
      }
    )

    const currentUser = computed(() => {
      try {
        return JSON.parse(localStorage.currentUser)
      } catch {
        return null
      }
    })

    const initials = computed(() => {
      if (!currentUser.value?.nombreUsuario) return 'U'
      return currentUser.value.nombreUsuario
        .split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    })

    const role = computed(() => {
      const perfil = currentUser.value?.perfil
      if (Array.isArray(perfil)) {
        return perfil.map(p => p.nombre).join(', ')
      }
      return perfil || ''
    })

    const openGroups = ref([])
    const flyout = ref(null)
    let closeTimer = null

    const scheduleClose = () => {
      closeTimer = setTimeout(() => {
        flyout.value = null
      }, 140)
    }

    const cancelClose = () => {
      if (closeTimer) {
        clearTimeout(closeTimer)
        closeTimer = null
      }
    }

    const openFlyout = (item, event) => {
      if (!collapsed.value) return
      cancelClose()
      const rect = event.currentTarget.getBoundingClientRect()
      const itemLength = item.items ? item.items.length : 1
      const maxTop = window.innerHeight - 80 - itemLength * 40
      flyout.value = {
        item,
        top: Math.max(12, Math.min(rect.top, maxTop)),
      }
    }

    const isChildActive = to => route.path === to || route.path.startsWith(to + '/')
    const isGroupActive = group => group.items?.some(c => isChildActive(c.to))

    const isItemActive = item => {
      if (item.type === 'link') {
        return isChildActive(item.to)
      }
      return isGroupActive(item)
    }

    const toggleGroup = id => {
      const idx = openGroups.value.indexOf(id)
      if (idx >= 0) openGroups.value.splice(idx, 1)
      else openGroups.value.push(id)
    }

    const toggleCollapse = () => store.commit('toggleUnfoldable')

    // Abrir el grupo activo al montar
    navItems.forEach(section => {
      section.items.forEach(item => {
        if (item.type === 'group' && isGroupActive(item)) {
          openGroups.value.push(item.id)
        }
      })
    })

    return {
      navItems,
      iconMap,
      isRenderSideBar,
      collapsed,
      sidebarVisible,
      closeSidebar,
      currentUser,
      initials,
      role,
      openGroups,
      isChildActive,
      isGroupActive,
      isItemActive,
      toggleGroup,
      toggleCollapse,
      route,
      flyout,
      openFlyout,
      scheduleClose,
      cancelClose,
    }
  },
}
</script>
